import InputText from '../InputText';
import isEmpty from 'is-empty';
import PasswordStrengthBar from 'react-password-strength-bar';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  hasLowerCase,
  hasMinLength,
  hasNumber,
  hasSpecialCharacter,
  hasUpperCase
} from '@utils/validators';
import { InputTextProps } from '../InputText/InputText';
import { useSession } from '@hooks';
import {
  faCheck,
  faEye,
  faEyeSlash,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';

interface InputPasswordProps extends React.InputHTMLAttributes<HTMLInputElement>, InputTextProps {
  showStrength?: boolean
  showViewPassword?: boolean
};


const languagesVarsCodes = [
  "LANG_neobooking_input_password_chars", //caractères
  "LANG_neobooking_input_password_1_uppercase_min", //1 majuscule minimum
  "LANG_neobooking_input_password_1_lowsercase_min", //1 minuscule minimum
  "LANG_neobooking_input_password_1_special_char", //1 caractère spécial (-()/{}:;,.?!@#$%&+=*)
  "LANG_neobooking_input_password_1_number_min", //1 chiffre minimum
  "LANG_neobooking_input_password_too_weak", //très faible
  "LANG_neobooking_input_password_weak", //faible
  "LANG_neobooking_input_password_medium", //moyen
  "LANG_neobooking_input_password_strong", //fort
  "LANG_neobooking_input_password_too_strong", //très fort
  "LANG_neobooking_input_password_too_short", //trop court
]

const InputPassword: React.FC<InputPasswordProps> = ({ showStrength, showViewPassword = true, ...props }) => {

  /* Hooks */
  const { getLanguageVarValue } = useSession();

  /* State */
  const [isPassword, setIsPassword] = useState<boolean>(true)
  const [isPasswordValid, setIsPasswordValid] = useState<boolean>(false);

  useEffect(() => {
    setIsPasswordValid(isValidPassword(props.value ? props.value.toString() : ""))
  }, [props.value?.toString()])

  const handleViewPassword = () => {
    setIsPassword(!isPassword)
  }

  const isValidPassword = (value: string) => {
    return hasMinLength(value, props.minLength ? props.minLength : 8) && hasUpperCase(value) && hasLowerCase(value) && hasSpecialCharacter(value) && hasNumber(value);
  }

  return <div>
    <div className="tw-relative">
      <InputText type={isPassword ? "password" : "text"} {...props} />
      {
        showViewPassword &&
        <button type="button" onClick={handleViewPassword} className="tw-flex tw-items-center tw-absolute tw-top-0 tw-bottom-0 tw-right-2">
          <FontAwesomeIcon icon={isPassword ? faEye : faEyeSlash} />
        </button>
      }
    </div>
    {
      (showStrength && !isEmpty(props.value)) && <div>
        <PasswordStrengthBar
          className="tw-block"
          scoreWords={[
            getLanguageVarValue("LANG_neobooking_input_password_too_weak"),
            getLanguageVarValue("LANG_neobooking_input_password_weak"),
            getLanguageVarValue("LANG_neobooking_input_password_medium"),
            getLanguageVarValue("LANG_neobooking_input_password_strong"),
            getLanguageVarValue("LANG_neobooking_input_password_too_strong")
          ]}
          password={props.value?.toString() || ""}
          shortScoreWord={getLanguageVarValue("LANG_neobooking_input_password_too_short")}
          minLength={props.minLength}
        />
        <div className="tw-bg-gray-100 tw-p-1">
          <RestrictedPasswordInfo value={props.value?.toString()} condition={() => hasMinLength(props.value?.toString() ?? "", props.minLength)}>
            {props.minLength ? props.minLength : 8}{" "}{getLanguageVarValue("LANG_neobooking_input_password_chars")}
          </RestrictedPasswordInfo>
          <RestrictedPasswordInfo value={props.value?.toString()} condition={hasUpperCase}>
            {getLanguageVarValue("LANG_neobooking_input_password_1_uppercase_min")}
          </RestrictedPasswordInfo>
          <RestrictedPasswordInfo value={props.value?.toString()} condition={hasLowerCase}>
            {getLanguageVarValue("LANG_neobooking_input_password_1_lowsercase_min")}
          </RestrictedPasswordInfo>
          <RestrictedPasswordInfo value={props.value?.toString()} condition={hasSpecialCharacter}>
            {getLanguageVarValue("LANG_neobooking_input_password_1_special_char")}
          </RestrictedPasswordInfo>
          <RestrictedPasswordInfo value={props.value?.toString()} condition={hasNumber}>
            {getLanguageVarValue("LANG_neobooking_input_password_1_number_min")}
          </RestrictedPasswordInfo>
        </div>
      </div>
    }
  </div>;
};

type RestrictedPasswordInfoProps = {
  value?: string,
  condition: (...args: any) => {},
}

const RestrictedPasswordInfo: React.FC<RestrictedPasswordInfoProps> = ({ value, condition, children }) => {
  const [isValidCondition, setIsValidCondition] = useState(condition(value))
  useEffect(() => {
    setIsValidCondition(condition(value))
  }, [value])

  return <div className={`tw-block`}>
    <div className={`tw-min-w-[25px] tw-inline-block ${isValidCondition ? "tw-text-green-500" : "tw-text-red-500"}`}><FontAwesomeIcon icon={isValidCondition ? faCheck : faTimes} /></div>
    <span>{children}</span>
  </div>
}

InputPassword.propTypes = {
  showStrength: PropTypes.bool,
  showViewPassword: PropTypes.bool
};


export default InputPassword;
